//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { chipColorForStatus } from './UnifiedCardData';
import DeploymentsIcon from '../../../icons/deploy_search.svg';
import { UnifiedCardData, formatDate, displayOrNA } from './UnifiedCardData';
import RelatedLinks from './RelatedLinks';

export const DeploymentCard: React.FC<{ card: UnifiedCardData }> = ({
  card,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Convert possible ISO string timestamps to epoch numbers for formatting
  const toEpoch = (val?: string) => {
    if (!val) return undefined;
    // If already numeric string
    const numeric = Number(val);
    if (!isNaN(numeric) && val.trim() !== '') return numeric;
    const parsed = Date.parse(val);
    return isNaN(parsed) ? undefined : parsed;
  };

  const formattedStart = formatDate(toEpoch(card.startTime));
  const formattedEnd = formatDate(toEpoch(card.endTime));

  return (
    <div
      className="mb-6 rounded-lg bg-[#f2f4f5] p-6 dark:bg-[#393939]"
      id="id-deployment-card-container"
      data-testid="deployment-card-container"
    >
      <div
        className="mb-4 flex items-start gap-3"
        id="id-deployment-card-content"
        data-testid="deployment-card-content"
      >
        <div
          className="mt-0.5 shrink-0"
          id="id-deployment-card-icon-wrapper"
          data-testid="deployment-card-icon-wrapper"
        >
          <div
            className="flex size-8 items-center justify-center rounded"
            id="id-deployment-card-icon"
            data-testid="deployment-card-icon"
          >
            <img
              src={DeploymentsIcon}
              alt={t('deployment')}
              className="size-8 object-contain"
            />
          </div>
        </div>
        <div
          className="min-w-0 flex-1"
          id="id-deployment-card-details"
          data-testid="deployment-card-details"
        >
          <div
            className="mb-1 flex items-center gap-2"
            id="id-deployment-card-title-row"
            data-testid="deployment-card-title-row"
          >
            <span
              className="text-[12px] text-gray-500 dark:text-gray-400"
              id="id-deployment-card-type-label"
              data-testid="deployment-card-type-label"
            >
              {t('deployment')}
            </span>
            <span
              className="text-[12px] text-gray-300 dark:text-gray-600"
              id="id-deployment-card-title-separator"
              data-testid="deployment-card-title-separator"
            >
              |
            </span>
            <span
              className="cursor-default text-[14px] font-medium text-[#0043ce] dark:text-brand-interactiveDark"
              id="id-deployment-card-title"
              data-testid="deployment-card-title"
            >
              {card.cardTitle}
            </span>
          </div>
          <div
            className="mt-1 text-[12px] text-gray-600 dark:text-gray-300"
            id="id-deployment-card-metadata-primary"
            data-testid="deployment-card-metadata-primary"
          >
            <span
              id="id-deployment-card-repo-name-label"
              data-testid="deployment-card-repo-name-label"
            >
              {t('repository-name')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-repo-name-value"
              data-testid="deployment-card-repo-name-value"
            >
              {displayOrNA(card.repositoryName, t)}
            </span>
            <span
              className="mx-1"
              id="id-deployment-card-separator-1"
              data-testid="deployment-card-separator-1"
            >
              |
            </span>
            <span
              id="id-deployment-card-repo-owner-label"
              data-testid="deployment-card-repo-owner-label"
            >
              {t('repository-owner')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-repo-owner-value"
              data-testid="deployment-card-repo-owner-value"
            >
              {displayOrNA(card.repositoryOwner, t)}
            </span>
            <span
              className="mx-1"
              id="id-deployment-card-separator-2"
              data-testid="deployment-card-separator-2"
            >
              |
            </span>
            <span
              id="id-deployment-card-base-branch-label"
              data-testid="deployment-card-base-branch-label"
            >
              {t('base-branch')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-base-branch-value"
              data-testid="deployment-card-base-branch-value"
            >
              {displayOrNA(card.baseBranch, t)}
            </span>
            <span
              className="mx-1"
              id="id-deployment-card-separator-3"
              data-testid="deployment-card-separator-3"
            >
              |
            </span>
            <span
              id="id-deployment-card-triggered-by-label"
              data-testid="deployment-card-triggered-by-label"
            >
              {t('triggered-by')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-triggered-by-value"
              data-testid="deployment-card-triggered-by-value"
            >
              {displayOrNA(card.triggeredBy, t)}
            </span>
            <span
              className="mx-1"
              id="id-deployment-card-separator-4"
              data-testid="deployment-card-separator-4"
            >
              |
            </span>
            <span
              id="id-deployment-card-environment-label"
              data-testid="deployment-card-environment-label"
            >
              {t('environment')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-environment-value"
              data-testid="deployment-card-environment-value"
            >
              {displayOrNA(card.environment, t)}
            </span>
            <span
              className="mx-1"
              id="id-deployment-card-separator-5"
              data-testid="deployment-card-separator-5"
            >
              |
            </span>
            <span
              id="id-deployment-card-application-label"
              data-testid="deployment-card-application-label"
            >
              {t('application')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-application-value"
              data-testid="deployment-card-application-value"
            >
              {displayOrNA(card.application, t)}
            </span>
            <span
              className="mx-1"
              id="id-deployment-card-separator-6"
              data-testid="deployment-card-separator-6"
            >
              |
            </span>
            <span
              id="id-deployment-card-loop-label"
              data-testid="deployment-card-loop-label"
            >
              {t('loop')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-loop-value"
              data-testid="deployment-card-loop-value"
            >
              {displayOrNA(card.loopName, t)}
            </span>
            {card.status && (
              <>
                <span
                  className="mx-1"
                  id="id-deployment-card-separator-7"
                  data-testid="deployment-card-separator-7"
                >
                  |
                </span>
                <span
                  id="id-deployment-card-status-label"
                  data-testid="deployment-card-status-label"
                >
                  {t('status')}:
                </span>
                <span
                  className={`ml-1 inline-flex h-[18px] items-center rounded-full px-2 text-[11px] font-medium ${chipColorForStatus(card.status)}`}
                  id="id-deployment-card-status-value"
                  data-testid="deployment-card-status-value"
                >
                  {card.status}
                </span>
              </>
            )}
          </div>
          <p className="mt-1 text-[12px] text-gray-600 dark:text-gray-300">
            <span
              id="id-deployment-card-version-label"
              data-testid="deployment-card-version-label"
            >
              {t('version')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-version-value"
              data-testid="deployment-card-version-value"
            >
              {displayOrNA(card.version, t)}
            </span>
            <span
              className="mx-1"
              id="id-deployment-card-separator-8"
              data-testid="deployment-card-separator-8"
            >
              |
            </span>
            <span
              id="id-deployment-card-start-time-label"
              data-testid="deployment-card-start-time-label"
            >
              {t('start-time')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-start-time-value"
              data-testid="deployment-card-start-time-value"
            >
              {formattedStart}
            </span>
            <span
              className="mx-1"
              id="id-deployment-card-separator-9"
              data-testid="deployment-card-separator-9"
            >
              |
            </span>
            <span
              id="id-deployment-card-end-time-label"
              data-testid="deployment-card-end-time-label"
            >
              {t('end-time')}:
            </span>
            <span
              className="ml-1"
              id="id-deployment-card-end-time-value"
              data-testid="deployment-card-end-time-value"
            >
              {formattedEnd}
            </span>
          </p>

          {/* Links Related - Accordion */}
          <div
            className="mt-4 border-t border-gray-300 pt-4 dark:border-gray-600"
            id="id-deployment-card-links-section"
            data-testid="deployment-card-links-section"
          >
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="flex items-center gap-2 text-[12px] text-[#0043ce] transition-colors hover:underline dark:text-brand-interactiveDark"
            >
              <span
                id="id-deployment-card-links-label"
                data-testid="deployment-card-links-label"
              >
                {t('links-related-result')}
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
            {expanded && (
              <RelatedLinks card={card} primaryLabel="deployment-url" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
